import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  submitBatteryBComplete,
  type StoryResponse,
  type SituationResponse,
  type ValuesResponse,
  type MiniSJTResponse,
} from '@/services/batteryB';

// ============================================
// STORAGE
// ============================================

const BATTERY_B_STORAGE_KEY = 'batteryB_progress';

interface SavedProgress {
  screen: Screen;
  storyIndex: number;
  sitIndex: number;
  sjtIndex: number;
  stories: string[];
  situations: Array<{ text: string; switches: Record<string, string> }>;
  selectedValues: string[];
  fork: string;
  horizon: string;
  miniSjt: Array<{ best: string; worst: string }>;
}

function loadProgress(): SavedProgress | null {
  try {
    const saved = localStorage.getItem(BATTERY_B_STORAGE_KEY);
    if (!saved) return null;
    return JSON.parse(saved);
  } catch {
    return null;
  }
}

function saveProgress(progress: SavedProgress): void {
  try {
    localStorage.setItem(BATTERY_B_STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Ignore storage errors
  }
}

function clearProgress(): void {
  try {
    localStorage.removeItem(BATTERY_B_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

// ============================================
// DATA
// ============================================

const STORY_PROMPTS = [
  { index: 1, imageEmoji: '🌅', prompt: 'Человек стоит на перекрёстке дорог на рассвете' },
  { index: 2, imageEmoji: '🏔️', prompt: 'Группа людей у подножия горы, один отходит в сторону' },
  { index: 3, imageEmoji: '🔥', prompt: 'Человек сидит у костра в темноте, рядом закрытая дверь' },
];

const SITUATION_PROMPTS = [
  'Вспомните ситуацию за последний месяц, когда вы чувствовали себя не в своей тарелке. Что произошло?',
  'Вспомните момент, когда вы приняли важное решение. Опишите, что вы чувствовали.',
];

const VALUE_OPTIONS = [
  { id: 'health', label: 'Здоровье', emoji: '💪' },
  { id: 'connections', label: 'Связи', emoji: '🤝' },
  { id: 'work', label: 'Дело', emoji: '🎯' },
  { id: 'creativity', label: 'Творчество', emoji: '✨' },
  { id: 'calm', label: 'Спокойствие', emoji: '🧘' },
  { id: 'meaning', label: 'Смысл', emoji: '🧭' },
];

const MINI_SJT_DATA = [
  {
    id: 'BSJT-1',
    situation: 'Коллега не выполнил обещание, из-за чего пострадал ваш общий проект.',
    options: [
      { id: 'A', text: 'Прямо скажу, что так нельзя, и предложу новые правила' },
      { id: 'B', text: 'Спрошу, что случилось — может, у него трудности' },
      { id: 'C', text: 'Промолчу и исправлю сам, чтобы не было конфликта' },
      { id: 'D', text: 'Выскажу своё недовольство и предложу пересмотреть роли' },
    ],
  },
  {
    id: 'BSJT-2',
    situation: 'Вам дали обратную связь, с которой вы категорически не согласны.',
    options: [
      { id: 'A', text: 'Спокойно выслушаю и проанализирую позже' },
      { id: 'B', text: 'Попрошу конкретные примеры и аргументирую свою позицию' },
      { id: 'C', text: 'Соглашусь вслух, чтобы не обострять' },
      { id: 'D', text: 'Открыто скажу, что считаю это несправедливым' },
    ],
  },
  {
    id: 'BSJT-3',
    situation: 'В компании кто-то отпустил неприятную шутку в ваш адрес.',
    options: [
      { id: 'A', text: 'Скажу, что мне это неприятно' },
      { id: 'B', text: 'Посмеюсь вместе — не стоит раздувать' },
      { id: 'C', text: 'Позже поговорю наедине, чтобы не смущать' },
      { id: 'D', text: 'Отвечу ещё более острой шуткой' },
    ],
  },
];

// ============================================
// TYPES
// ============================================

type Screen = 'intro' | 'b1' | 'b2' | 'b3_values' | 'b3_fork' | 'b3_horizon' | 'b4' | 'loading' | 'done';

interface Props {
  onComplete: () => void;
  onClose: () => void;
}

// ============================================
// MAIN COMPONENT
// ============================================

export function BatteryB({ onComplete, onClose }: Props) {
  const [screen, setScreen] = useState<Screen>('intro');
  const [error, setError] = useState<string | null>(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [pendingProgress, setPendingProgress] = useState<SavedProgress | null>(null);

  // B1 state
  const [stories, setStories] = useState<string[]>(['', '', '']);
  const [storyIndex, setStoryIndex] = useState(0);

  // B2 state
  const [situations, setSituations] = useState<Array<{
    text: string;
    switches: Record<string, string>;
  }>>([
    { text: '', switches: { social: '', intensity: '', voice: '', action: '' } },
    { text: '', switches: { social: '', intensity: '', voice: '', action: '' } },
  ]);
  const [sitIndex, setSitIndex] = useState(0);

  // B3 state
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [fork, setFork] = useState<'freedom' | 'stability' | ''>('');
  const [horizon, setHorizon] = useState<'week' | 'month' | '3months' | ''>('');

  // B4 state
  const [miniSjt, setMiniSjt] = useState<Array<{ best: string; worst: string }>>([
    { best: '', worst: '' },
    { best: '', worst: '' },
    { best: '', worst: '' },
  ]);
  const [sjtIndex, setSjtIndex] = useState(0);

  // Check for saved progress on mount
  useEffect(() => {
    const saved = loadProgress();
    if (saved && saved.screen !== 'intro') {
      setPendingProgress(saved);
      setShowResumeModal(true);
    }
  }, []);

  // Save progress when data changes (skip intro/loading/done screens)
  useEffect(() => {
    if (screen === 'intro' || screen === 'loading' || screen === 'done') return;
    saveProgress({
      screen,
      storyIndex,
      sitIndex,
      sjtIndex,
      stories,
      situations,
      selectedValues,
      fork,
      horizon,
      miniSjt,
    });
  }, [screen, storyIndex, sitIndex, sjtIndex, stories, situations, selectedValues, fork, horizon, miniSjt]);

  const restoreProgress = () => {
    if (!pendingProgress) return;
    setScreen(pendingProgress.screen);
    setStoryIndex(pendingProgress.storyIndex);
    setSitIndex(pendingProgress.sitIndex);
    setSjtIndex(pendingProgress.sjtIndex);
    setStories(pendingProgress.stories);
    setSituations(pendingProgress.situations);
    setSelectedValues(pendingProgress.selectedValues);
    setFork(pendingProgress.fork as 'freedom' | 'stability' | '');
    setHorizon(pendingProgress.horizon as 'week' | 'month' | '3months' | '');
    setMiniSjt(pendingProgress.miniSjt);
    setShowResumeModal(false);
    setPendingProgress(null);
  };

  const startFresh = () => {
    clearProgress();
    setShowResumeModal(false);
    setPendingProgress(null);
    setScreen('b1');
  };

  const totalSteps = 3 + 2 + 3 + 3; // stories + situations + values screens + mini-sjt
  const currentStep = (() => {
    if (screen === 'b1') return storyIndex + 1;
    if (screen === 'b2') return 3 + sitIndex + 1;
    if (screen === 'b3_values') return 6;
    if (screen === 'b3_fork') return 7;
    if (screen === 'b3_horizon') return 8;
    if (screen === 'b4') return 8 + sjtIndex + 1;
    return 0;
  })();

  // ============================================
  // SUBMIT
  // ============================================

  const handleSubmit = async () => {
    setScreen('loading');
    setError(null);

    try {
      const storyData: StoryResponse[] = stories.map((text, i) => ({
        imageIndex: i + 1,
        text,
      }));

      const sitData: SituationResponse[] = situations.map((s, i) => ({
        index: i + 1,
        text: s.text,
        switches: s.switches as SituationResponse['switches'],
      }));

      const valData: ValuesResponse = {
        topValues: selectedValues,
        fork: fork as 'freedom' | 'stability',
        horizon: horizon as 'week' | 'month' | '3months',
      };

      const sjtData: MiniSJTResponse[] = miniSjt.map((s, i) => ({
        question: MINI_SJT_DATA[i].id,
        best: s.best,
        worst: s.worst,
      }));

      await submitBatteryBComplete({
        stories: storyData,
        situations: sitData,
        values: valData,
        miniSjt: sjtData,
      });

      // Clear saved progress after successful completion
      clearProgress();
      setScreen('done');
    } catch (err: unknown) {
      setError((err as Error).message);
      setScreen('b4');
    }
  };

  // ============================================
  // RENDER
  // ============================================

  // Resume modal
  if (showResumeModal && pendingProgress) {
    const stepName = (() => {
      switch (pendingProgress.screen) {
        case 'b1': return `история ${pendingProgress.storyIndex + 1}/3`;
        case 'b2': return `ситуация ${pendingProgress.sitIndex + 1}/2`;
        case 'b3_values': return 'выбор ценностей';
        case 'b3_fork': return 'развилка';
        case 'b3_horizon': return 'горизонт планирования';
        case 'b4': return `мини-SJT ${pendingProgress.sjtIndex + 1}/3`;
        default: return 'прогресс';
      }
    })();

    return (
      <ScreenWrapper>
        <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
          <span className="text-5xl mb-4">📝</span>
          <h1 className="text-xl font-bold text-white mb-2">Продолжить Батарею B?</h1>
          <p className="text-primary-400 text-sm mb-6">
            Вы остановились на: <span className="text-indigo-400">{stepName}</span>
          </p>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={restoreProgress}
            className="w-full max-w-xs py-3 rounded-xl bg-indigo-600 text-white font-medium mb-3"
          >
            Продолжить
          </motion.button>
          <button
            onClick={startFresh}
            className="text-primary-500 text-sm"
          >
            Начать заново
          </button>
        </div>
      </ScreenWrapper>
    );
  }

  if (screen === 'intro') {
    return (
      <ScreenWrapper>
        <div className="flex flex-col min-h-screen">
          {/* Header at top */}
          <div className="p-4 text-center">
            <h1 className="text-2xl font-bold text-white">Диагностика B</h1>
            <p className="text-primary-400 text-sm">Углублённое профилирование</p>
          </div>

          {/* Large visual - sunrise at crossroads */}
          <div className="flex-1 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-amber-900/30 via-orange-800/20 to-primary-950" />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-primary-950 to-transparent" />

            {/* Crossroads visual */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                {/* Sun */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1 }}
                  className="text-8xl mb-8"
                >
                  🌅
                </motion.div>

                {/* Crossroads paths */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-4xl flex gap-8"
                >
                  <span className="opacity-60">🛤️</span>
                  <span>🚶</span>
                  <span className="opacity-60">🛤️</span>
                </motion.div>
              </div>
            </div>

            {/* Decorative stars */}
            <div className="absolute top-10 left-10 text-xl opacity-30">✦</div>
            <div className="absolute top-20 right-16 text-sm opacity-20">✧</div>
            <div className="absolute top-32 left-24 text-xs opacity-25">✦</div>
          </div>

          {/* Bottom section with description and button */}
          <div className="p-6 text-center">
            <p className="text-primary-500 text-xs mb-2">4 блока, ~6-8 минут</p>
            <p className="text-primary-400 text-sm mb-6 max-w-xs mx-auto">
              Истории по картинкам, жизненные ситуации, ценности и границы.
              Это поможет точнее определить ваш архетип.
            </p>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setScreen('b1')}
              className="w-full max-w-xs py-3 rounded-xl bg-indigo-600 text-white font-medium"
            >
              Начать
            </motion.button>
            <button onClick={onClose} className="mt-4 text-primary-500 text-sm">Позже</button>
          </div>
        </div>
      </ScreenWrapper>
    );
  }

  if (screen === 'loading') {
    return (
      <ScreenWrapper>
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            className="w-12 h-12 rounded-full border-3 border-primary-700 border-t-indigo-400 mb-4"
          />
          <p className="text-primary-400 text-sm">AI анализирует ваши ответы...</p>
        </div>
      </ScreenWrapper>
    );
  }

  if (screen === 'done') {
    return (
      <ScreenWrapper>
        <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
          <span className="text-5xl mb-4">✅</span>
          <h1 className="text-xl font-bold text-white mb-3">Диагностика B пройдена!</h1>

          <div className="w-full max-w-xs mb-6 space-y-3">
            <div className="p-4 rounded-xl bg-primary-800/30 border border-primary-700/50">
              <p className="text-sm text-primary-300 mb-2">Мы уточнили твой профиль на основе:</p>
              <ul className="text-xs text-primary-400 space-y-1 text-left">
                <li>📖 Проективных историй по картинкам</li>
                <li>🎭 Анализа жизненных ситуаций</li>
                <li>💎 Выбранных ценностей и приоритетов</li>
                <li>⚖️ Реакций на сложные выборы</li>
              </ul>
            </div>

            <div className="p-3 rounded-xl bg-indigo-900/20 border border-indigo-800/30">
              <p className="text-xs text-indigo-300">
                Твои характеристики обновлены. Посмотри изменения в профиле Персонажа.
              </p>
            </div>
          </div>

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onComplete}
            className="w-full max-w-xs py-3 rounded-xl bg-indigo-600 text-white font-medium"
          >
            Готово
          </motion.button>
        </div>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      {/* Progress bar */}
      <div className="p-4 border-b border-primary-800/50">
        <div className="flex items-center justify-between text-xs text-primary-500 mb-1">
          <span>Диагностика B</span>
          <span>{currentStep}/{totalSteps}</span>
        </div>
        <div className="h-1.5 bg-primary-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-indigo-500 rounded-full"
            animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 p-2 rounded-lg bg-red-900/30 border border-red-800/30">
          <p className="text-xs text-red-400 text-center">{error}</p>
        </div>
      )}

      <div className="p-4 overflow-y-auto flex-1">
        {/* B1: Stories */}
        {screen === 'b1' && (
          <B1Story
            prompt={STORY_PROMPTS[storyIndex]}
            value={stories[storyIndex]}
            onChange={(text) => {
              const next = [...stories];
              next[storyIndex] = text;
              setStories(next);
            }}
            onNext={() => {
              if (storyIndex < 2) { setStoryIndex(storyIndex + 1); }
              else { setSitIndex(0); setScreen('b2'); }
            }}
            canNext={stories[storyIndex].length >= 50}
          />
        )}

        {/* B2: Situations */}
        {screen === 'b2' && (
          <B2Situation
            prompt={SITUATION_PROMPTS[sitIndex]}
            data={situations[sitIndex]}
            onChange={(data) => {
              const next = [...situations];
              next[sitIndex] = data;
              setSituations(next);
            }}
            onNext={() => {
              if (sitIndex < 1) setSitIndex(sitIndex + 1);
              else setScreen('b3_values');
            }}
            canNext={situations[sitIndex].text.length > 10 && Object.values(situations[sitIndex].switches).every(v => v !== '')}
          />
        )}

        {/* B3: Values screen 1 */}
        {screen === 'b3_values' && (
          <B3Values
            options={VALUE_OPTIONS}
            selected={selectedValues}
            onToggle={(id) => {
              if (selectedValues.includes(id)) {
                setSelectedValues(selectedValues.filter(v => v !== id));
              } else if (selectedValues.length < 2) {
                setSelectedValues([...selectedValues, id]);
              }
            }}
            onNext={() => setScreen('b3_fork')}
            canNext={selectedValues.length === 2}
          />
        )}

        {/* B3: Fork */}
        {screen === 'b3_fork' && (
          <B3Fork
            value={fork}
            onChange={(v) => setFork(v as 'freedom' | 'stability')}
            onNext={() => setScreen('b3_horizon')}
          />
        )}

        {/* B3: Horizon */}
        {screen === 'b3_horizon' && (
          <B3Horizon
            value={horizon}
            onChange={(v) => setHorizon(v as 'week' | 'month' | '3months')}
            onNext={() => { setSjtIndex(0); setScreen('b4'); }}
          />
        )}

        {/* B4: Mini-SJT */}
        {screen === 'b4' && (
          <B4MiniSJT
            data={MINI_SJT_DATA[sjtIndex]}
            response={miniSjt[sjtIndex]}
            onChange={(r) => {
              const next = [...miniSjt];
              next[sjtIndex] = r;
              setMiniSjt(next);
            }}
            onNext={() => {
              if (sjtIndex < 2) setSjtIndex(sjtIndex + 1);
              else handleSubmit();
            }}
            canNext={miniSjt[sjtIndex].best !== '' && miniSjt[sjtIndex].worst !== '' && miniSjt[sjtIndex].best !== miniSjt[sjtIndex].worst}
          />
        )}
      </div>
    </ScreenWrapper>
  );
}

function ScreenWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-primary-950 safe-area-top safe-area-bottom flex flex-col">
      {children}
    </div>
  );
}

// ============================================
// B1: STORY SCREEN
// ============================================

function B1Story({ prompt, value, onChange, onNext, canNext }: {
  prompt: { index: number; imageEmoji: string; prompt: string };
  value: string;
  onChange: (text: string) => void;
  onNext: () => void;
  canNext: boolean;
}) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <div className="text-center">
        <span className="text-5xl block mb-2">{prompt.imageEmoji}</span>
        <p className="text-sm text-primary-400 italic">{prompt.prompt}</p>
      </div>
      <div>
        <p className="text-sm text-primary-300 mb-2">
          Придумайте историю: что происходит, чего хочет герой, что сделает, чем закончится?
        </p>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={450}
          rows={6}
          placeholder="Расскажите историю..."
          className="w-full bg-primary-800/50 border border-primary-700/50 rounded-xl p-3 text-white text-sm placeholder-primary-600 resize-none focus:outline-none focus:border-indigo-500/50"
        />
        <p className="text-xs text-primary-600 text-right">{value.length}/450</p>
      </div>
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onNext}
        disabled={!canNext}
        className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
      >
        Далее
      </motion.button>
    </motion.div>
  );
}

// ============================================
// B2: SITUATION SCREEN
// ============================================

function B2Situation({ prompt, data, onChange, onNext, canNext }: {
  prompt: string;
  data: { text: string; switches: Record<string, string> };
  onChange: (d: { text: string; switches: Record<string, string> }) => void;
  onNext: () => void;
  canNext: boolean;
}) {
  const toggleSwitch = (key: string, value: string) => {
    onChange({ ...data, switches: { ...data.switches, [key]: value } });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <p className="text-sm text-primary-300">{prompt}</p>
      <textarea
        value={data.text}
        onChange={(e) => onChange({ ...data, text: e.target.value })}
        maxLength={400}
        rows={4}
        placeholder="Опишите ситуацию..."
        className="w-full bg-primary-800/50 border border-primary-700/50 rounded-xl p-3 text-white text-sm placeholder-primary-600 resize-none focus:outline-none focus:border-indigo-500/50"
      />
      <p className="text-xs text-primary-600 text-right">{data.text.length}/400</p>

      <div className="space-y-2">
        <SwitchRow label="Контекст" optA="Один" optB="С людьми" k="social" vA="solo" vB="with_people" current={data.switches.social} onToggle={toggleSwitch} />
        <SwitchRow label="Интенсивность" optA="Спокойно" optB="Интенсивно" k="intensity" vA="calm" vB="intense" current={data.switches.intensity} onToggle={toggleSwitch} />
        <SwitchRow label="Голос" optA="Говорил" optB="Промолчал" k="voice" vA="spoke" vB="silent" current={data.switches.voice} onToggle={toggleSwitch} />
        <SwitchRow label="Действие" optA="Ушёл" optB="Остался" k="action" vA="left" vB="stayed" current={data.switches.action} onToggle={toggleSwitch} />
      </div>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onNext}
        disabled={!canNext}
        className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
      >
        Далее
      </motion.button>
    </motion.div>
  );
}

function SwitchRow({ label, optA, optB, k, vA, vB, current, onToggle }: {
  label: string; optA: string; optB: string; k: string; vA: string; vB: string; current: string;
  onToggle: (k: string, v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-primary-500 w-24 flex-shrink-0">{label}</span>
      <button
        onClick={() => onToggle(k, vA)}
        className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
          current === vA ? 'bg-indigo-900/40 border-indigo-700/50 text-indigo-300' : 'bg-primary-800/30 border-primary-700/50 text-primary-400'
        }`}
      >{optA}</button>
      <button
        onClick={() => onToggle(k, vB)}
        className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
          current === vB ? 'bg-indigo-900/40 border-indigo-700/50 text-indigo-300' : 'bg-primary-800/30 border-primary-700/50 text-primary-400'
        }`}
      >{optB}</button>
    </div>
  );
}

// ============================================
// B3: VALUES
// ============================================

function B3Values({ options, selected, onToggle, onNext, canNext }: {
  options: Array<{ id: string; label: string; emoji: string }>;
  selected: string[];
  onToggle: (id: string) => void;
  onNext: () => void;
  canNext: boolean;
}) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <p className="text-sm text-primary-300">Выберите 2 самые важные ценности прямо сейчас:</p>
      <div className="grid grid-cols-2 gap-2">
        {options.map(o => (
          <button
            key={o.id}
            onClick={() => onToggle(o.id)}
            className={`p-3 rounded-xl text-left border transition-colors ${
              selected.includes(o.id)
                ? 'bg-indigo-900/40 border-indigo-700/50'
                : 'bg-primary-800/30 border-primary-700/50'
            }`}
          >
            <span className="text-xl block mb-1">{o.emoji}</span>
            <span className={`text-sm ${selected.includes(o.id) ? 'text-white' : 'text-primary-400'}`}>{o.label}</span>
          </button>
        ))}
      </div>
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onNext}
        disabled={!canNext}
        className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
      >
        Далее
      </motion.button>
    </motion.div>
  );
}

function B3Fork({ value, onChange, onNext }: {
  value: string;
  onChange: (v: string) => void;
  onNext: () => void;
}) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <p className="text-sm text-primary-300">Что важнее прямо сейчас?</p>
      <div className="space-y-2">
        <ChoiceCard selected={value === 'freedom'} onClick={() => onChange('freedom')} emoji="🦅" label="Свобода" desc="Возможности, независимость, новые пути" />
        <ChoiceCard selected={value === 'stability'} onClick={() => onChange('stability')} emoji="🏠" label="Стабильность" desc="Надёжность, опора, понятность" />
      </div>
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onNext}
        disabled={!value}
        className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
      >
        Далее
      </motion.button>
    </motion.div>
  );
}

function B3Horizon({ value, onChange, onNext }: {
  value: string;
  onChange: (v: string) => void;
  onNext: () => void;
}) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <p className="text-sm text-primary-300">На какой горизонт вы сейчас планируете?</p>
      <div className="space-y-2">
        <ChoiceCard selected={value === 'week'} onClick={() => onChange('week')} emoji="📅" label="Неделя" desc="Ближайшие дни, конкретные шаги" />
        <ChoiceCard selected={value === 'month'} onClick={() => onChange('month')} emoji="📆" label="Месяц" desc="Среднесрочные цели и привычки" />
        <ChoiceCard selected={value === '3months'} onClick={() => onChange('3months')} emoji="🗓️" label="3 месяца" desc="Стратегия, трансформация, большие цели" />
      </div>
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onNext}
        disabled={!value}
        className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
      >
        Далее
      </motion.button>
    </motion.div>
  );
}

function ChoiceCard({ selected, onClick, emoji, label, desc }: {
  selected: boolean; onClick: () => void; emoji: string; label: string; desc: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-xl text-left border transition-colors ${
        selected ? 'bg-indigo-900/40 border-indigo-700/50' : 'bg-primary-800/30 border-primary-700/50'
      }`}
    >
      <span className="text-xl mr-2">{emoji}</span>
      <span className={`font-medium ${selected ? 'text-white' : 'text-primary-300'}`}>{label}</span>
      <p className="text-xs text-primary-500 mt-1 ml-8">{desc}</p>
    </button>
  );
}

// ============================================
// B4: MINI-SJT
// ============================================

function B4MiniSJT({ data, response, onChange, onNext, canNext }: {
  data: { id: string; situation: string; options: Array<{ id: string; text: string }> };
  response: { best: string; worst: string };
  onChange: (r: { best: string; worst: string }) => void;
  onNext: () => void;
  canNext: boolean;
}) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <div className="rounded-xl bg-primary-800/30 border border-primary-700/50 p-3">
        <p className="text-sm text-white">{data.situation}</p>
      </div>

      <p className="text-xs text-primary-500">Выберите лучший (+) и худший (-) вариант:</p>

      <div className="space-y-2">
        {data.options.map(opt => {
          const isBest = response.best === opt.id;
          const isWorst = response.worst === opt.id;
          return (
            <div key={opt.id} className="flex items-center gap-2">
              <button
                onClick={() => onChange({ ...response, best: isBest ? '' : opt.id, worst: response.worst === opt.id ? '' : response.worst })}
                className={`w-8 h-8 rounded-full text-xs font-bold flex-shrink-0 border ${
                  isBest ? 'bg-green-900/50 border-green-600 text-green-300' : 'bg-primary-800/30 border-primary-700/50 text-primary-500'
                }`}
              >+</button>
              <div className={`flex-1 p-2 rounded-lg text-sm ${isBest ? 'text-green-300' : isWorst ? 'text-red-300' : 'text-primary-300'}`}>
                {opt.text}
              </div>
              <button
                onClick={() => onChange({ ...response, worst: isWorst ? '' : opt.id, best: response.best === opt.id ? '' : response.best })}
                className={`w-8 h-8 rounded-full text-xs font-bold flex-shrink-0 border ${
                  isWorst ? 'bg-red-900/50 border-red-600 text-red-300' : 'bg-primary-800/30 border-primary-700/50 text-primary-500'
                }`}
              >-</button>
            </div>
          );
        })}
      </div>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onNext}
        disabled={!canNext}
        className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
      >
        {canNext ? 'Далее' : 'Выберите лучший и худший'}
      </motion.button>
    </motion.div>
  );
}
